import {
  AI_ASSETS,
  AI_ASSET_IDS,
  AI_OBJECTS,
} from "@endclock/shared";
import type { Camera } from "../engine/camera.js";
import type { Renderer } from "../engine/renderer.js";
import { AIAssetLibrary, type AILoadState } from "./asset-library.js";
export type { AILoadState } from "./asset-library.js";
import { createStaticObjectChunks } from "../world/static-instances.js";

/** Phase 7.1 human-origin facility layer reusing the approved static GLB, instance and terrain-chunk pipeline. */
export class AIWorld {
  objects: readonly (typeof AI_OBJECTS)[number][] = AI_OBJECTS;
  get state(): AILoadState { return this.library.state; }
  get loadError(): string | undefined { return this.library.loadError; }
  private get assets(): AIAssetLibrary["assets"] { return this.library.assets; }
  private chunks = createStaticObjectChunks(this.objects, (object) => {
    const definition = AI_ASSETS[object.assetId];
    return {
      centerY: object.y + definition.boundsCenterY * object.scale,
      radius: definition.boundsRadius * object.scale,
    };
  });

  constructor(private readonly renderer: Renderer, readonly library = new AIAssetLibrary(renderer)) {
    library.setStaticTotals(this.objects.length, 0);
    void library.ready.then(() => {
      if (this.state === "ready") this.setObjects(this.objects);
    });
  }

  setObjects(objects: readonly (typeof AI_OBJECTS)[number][]): void {
    this.renderer.invalidateLocalShadowCache();
    this.objects = objects;
    this.chunks = createStaticObjectChunks(objects, (object) => {
      const definition = AI_ASSETS[object.assetId];
      return {
        centerY: object.y + definition.boundsCenterY * object.scale,
        radius: definition.boundsRadius * object.scale,
      };
    });
    this.library.setStaticTotals(objects.length, 0);
    if (this.state === "ready") {
      this.library.setStaticTotals(objects.length, objects.reduce(
        (sum, object) => sum + (this.assets.get(object.assetId)?.triangleCount ?? 0),
        0,
      ));
    }
  }

  render(camera: Camera): void {
    if (this.state !== "ready") return;
    for (const asset of this.assets.values()) {
      for (const primitive of asset.primitives) {
        primitive.activeBatch.reset();
        primitive.inactiveBatch.reset();
      }
    }

    for (const chunk of this.chunks) {
      if (!camera.isSphereVisible(chunk.centerX, chunk.centerY, chunk.centerZ, chunk.radius)) continue;
      for (const object of chunk.objects) {
        const asset = this.assets.get(object.assetId);
        if (!asset) continue;
        const centerY = object.y + asset.boundsCenterY * object.scale;
        if (!camera.isSphereVisible(object.x, centerY, object.z, asset.boundsRadius * object.scale)) continue;
        for (const primitive of asset.primitives) {
          const batch = object.active ? primitive.activeBatch : primitive.inactiveBatch;
          if (object.terrainAlignment > 0) {
            batch.pushTerrainAligned(
              object,
              object.terrainNormal,
              object.terrainAlignment,
              primitive.baseColor,
              0.018,
              0.82,
            );
          } else {
            batch.pushUpright(object, primitive.baseColor, 0.018, 0.82);
          }
        }
        this.renderer.metrics.visibleAiObjects += 1;
      }
    }

    for (const assetId of AI_ASSET_IDS) {
      const asset = this.assets.get(assetId);
      if (!asset) continue;
      for (const primitive of asset.primitives) {
        this.renderer.drawStaticInstanced(
          primitive.mesh,
          primitive.activeBatch.data,
          primitive.activeBatch.count,
          primitive.activeMaterial,
          "ai",
        );
        this.renderer.drawStaticInstanced(
          primitive.mesh,
          primitive.inactiveBatch.data,
          primitive.inactiveBatch.count,
          primitive.inactiveMaterial,
          "ai",
        );
      }
    }
  }

}
